import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './App.css'; 
import ContactModal from './components/modals/ContactModal'; 

// --- TYPES ---
interface Project {
  title: string;
  location: string;
  status: string;
  color: string;
  image: string;
  stat: string;
}

interface CityData {
  name: string;
  projects: Project[];
}

// --- CONFIGURATION ---
const LOOPS_FORM_ID = "cmjojm9150xwh0ivf9dbemo6b"; 

// --- SECURITY: BLOCKLIST FOR DISPOSABLE EMAILS ---
const DISPOSABLE_DOMAINS = [
  "mailinator.com", "yopmail.com", "guerrillamail.com", "temp-mail.org", 
  "10minutemail.com", "sharklasers.com", "throwawaymail.com", "getairmail.com",
  "maildrop.cc", "nopost.com", "boun.cr", "mailcatch.com", "trashmail.com",
  "tempmail.net", "dispostable.com", "fake-email.com", "emailondeck.com"
];

// --- ICONS ---
const ArrowRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"></line>
    <polyline points="12 5 19 12 12 19"></polyline>
  </svg>
);

const PlusIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0D47A1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const MinusIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0D47A1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const CheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);

// --- DATA PAYLOAD ---
const MARKET_DATA: CityData[] = [
  {
    name: "New York City Metro",
    projects: [
      { title: "Long Island City Tower", location: "Long Island City • Queens", status: "Public Hearing Scheduled", color: "red", image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=800&q=80", stat: "68 Stories • 800 Units" },
      { title: "Bronx Logistics Center", location: "Hunts Point • Bronx", status: "Traffic Impact Study Review", color: "yellow", image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=800&q=80", stat: "400,000 Square Feet • 24/7 Operations" },
      { title: "Gowanus Green Rezoning", location: "Gowanus • Brooklyn", status: "ULURP Approved", color: "green", image: "https://images.unsplash.com/photo-1534237710431-e2fc698436d0?auto=format&fit=crop&w=800&q=80", stat: "Mixed-Use • Site Remediation" },
      { title: "Harlem Life Sciences Center", location: "Harlem • Manhattan", status: "Demolition Permit Issued", color: "blue", image: "https://images.unsplash.com/photo-1581093450021-4a7360e9a6b5?auto=format&fit=crop&w=800&q=80", stat: "Lab Building • Biotech" }
    ]
  },
  {
    name: "Los Angeles Metro",
    projects: [
      { title: "NoHo Transit Village", location: "North Hollywood • Los Angeles", status: "Grading Permit Issued", color: "blue", image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=800&q=80", stat: "1,500 Units • Metro Adjacent" },
      { title: "Sunset Boulevard Hotel", location: "West Hollywood, CA", status: "Conditional Use Permit", color: "red", image: "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=800&q=80", stat: "Height & Alcohol Permit" },
      { title: "Silver Lake ADU Cluster", location: "Silver Lake • Los Angeles", status: "Plan Check Approved", color: "green", image: "https://images.unsplash.com/photo-1493809842364-78817add7ffb?auto=format&fit=crop&w=800&q=80", stat: "Infill Density • 4 Units" },
      { title: "Arts District Studio", location: "Arts District • Los Angeles", status: "Design Commission Review", color: "yellow", image: "/assets/ArtsDistrictStudio.jpeg", stat: "Production Stage Campus" }
    ]
  },
  {
    name: "Chicago Metro",
    projects: [
      { title: "O'Hare Logistics Park", location: "Des Plaines, IL", status: "Foundation Permit Issued", color: "blue", image: "https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=800&q=80", stat: "Freight Hub • Noise Impact" },
      { title: "Fulton Market Conversion", location: "West Loop • Chicago", status: "Zoning Map Amendment", color: "yellow", image: "https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?auto=format&fit=crop&w=800&q=80", stat: "Loft Office • Adaptive Reuse" },
      { title: "Lincoln Yards North", location: "Lincoln Park • Chicago", status: "Master Plan Review", color: "red", image: "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?auto=format&fit=crop&w=800&q=80", stat: "Riverfront • Mega-Development" },
      { title: "South Loop Stadium", location: "South Loop • Chicago", status: "Community Meeting", color: "red", image: "/assets/SouthLoopStadium.jpeg", stat: "Sports Arena • Traffic Impact" }
    ]
  },
  {
    name: "Houston Metro",
    projects: [
      { title: "Energy Corridor Headquarters", location: "Energy Corridor • Houston", status: "Final Plat Recorded", color: "green", image: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80", stat: "50 Acres • Office Park" },
      { title: "Port Houston Terminal", location: "Ship Channel • Houston", status: "Special Use Permit", color: "red", image: "https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?auto=format&fit=crop&w=800&q=80", stat: "Hazmat Route • Cranes" },
      { title: "Bridgeland West", location: "Cypress, TX", status: "Site Development Permit", color: "blue", image: "https://images.unsplash.com/photo-1591955506264-3f5a6834570a?auto=format&fit=crop&w=800&q=80", stat: "3,000 Homes • Excavation" },
      { title: "TMC BioPort", location: "Med Center • Houston", status: "Variance Granted", color: "green", image: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=800&q=80", stat: "Medical Research Facility" }
    ]
  },
  {
    name: "Phoenix Metro",
    projects: [
      { title: "Stratus AI Data Campus", location: "Goodyear, AZ", status: "Stipulated Zoning Approval", color: "green", image: "https://images.unsplash.com/photo-1597852074816-d933c7d2b988?auto=format&fit=crop&w=800&q=80", stat: "800 Megawatts • Critical Infrastructure" },
      { title: "TSMC Supplier Fab", location: "North Phoenix • Phoenix", status: "Building Permit Issued", color: "blue", image: "https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?auto=format&fit=crop&w=800&q=80", stat: "Factory • Semiconductor" },
      { title: "Camelback Condo Tower", location: "Scottsdale, AZ", status: "Height Variance Request", color: "red", image: "https://images.unsplash.com/photo-1574362848149-11496d93a7c7?auto=format&fit=crop&w=800&q=80", stat: "Luxury Condo • Shadow Study" },
      { title: "Downtown University Hub", location: "Downtown • Phoenix", status: "Structural Permit Active", color: "blue", image: "https://images.unsplash.com/photo-1498243691581-b145c3f54a5a?auto=format&fit=crop&w=800&q=80", stat: "Education Campus" }
    ]
  },
  {
    name: "Philadelphia Metro",
    projects: [
      { title: "Schuylkill Yards Laboratory", location: "University City • Philadelphia", status: "Building Permit Active", color: "blue", image: "https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?auto=format&fit=crop&w=800&q=80", stat: "Glass Lab • Biotech" },
      { title: "Navy Yard Master Plan", location: "Navy Yard • Philadelphia", status: "Planning Commission Review", color: "yellow", image: "/assets/NavyYardMasterPlan.jpeg", stat: "1,200 Units • Waterfront" },
      { title: "Fishtown Townhomes", location: "Fishtown • Philadelphia", status: "ZBA Hearing Scheduled", color: "red", image: "https://images.unsplash.com/photo-1605276374104-dee2a0ed3cd6?auto=format&fit=crop&w=800&q=80", stat: "Rowhomes • Infill" },
      { title: "Northern Liberties Retail", location: "Northern Liberties • Philadelphia", status: "Final Site Plan Approval", color: "green", image: "https://images.unsplash.com/photo-1555529733-0e670560f7e1?auto=format&fit=crop&w=800&q=80", stat: "Shopping Plaza" }
    ]
  },
  {
    name: "San Antonio Metro",
    projects: [
      { title: "Pearl District Expansion", location: "Tobin Hill • San Antonio", status: "HDRC Approval", color: "green", image: "/assets/PearlDistrictExpansion.jpeg", stat: "Brick Renovation • Retail" },
      { title: "Southside Industrial Park", location: "Brooks City Base • San Antonio", status: "Plan Review in Progress", color: "yellow", image: "https://images.unsplash.com/photo-1599939571322-792a326991f2?auto=format&fit=crop&w=800&q=80", stat: "Warehouse • Manufacturing" },
      { title: "Hill Country Resort", location: "Far North • San Antonio", status: "Environmental Assessment", color: "red", image: "https://images.unsplash.com/photo-1535131749006-b7f58c99034b?auto=format&fit=crop&w=800&q=80", stat: "500 Acres • Golf Course" },
      { title: "Tech Port Arena", location: "Port San Antonio • San Antonio", status: "Certificate of Occupancy Pending", color: "blue", image: "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?auto=format&fit=crop&w=800&q=80", stat: "Esports Venue • Arena" }
    ]
  },
  {
    name: "San Diego Metro",
    projects: [
      { title: "Torrey Pines Research", location: "Del Mar, CA", status: "Coastal Development Permit", color: "red", image: "https://images.unsplash.com/photo-1554469384-e58fac16e23a?auto=format&fit=crop&w=800&q=80", stat: "Campus • Life Sciences" },
      { title: "Midway District Revitalization", location: "Midway • San Diego", status: "Community Plan Amendment", color: "yellow", image: "/assets/MidwayDistRevitaliz.jpeg", stat: "4,000 Units • Arena District" },
      { title: "North Park Upzoning", location: "North Park • San Diego", status: "Ordinance Adopted", color: "green", image: "https://images.unsplash.com/photo-1460317442991-0ec209397118?auto=format&fit=crop&w=800&q=80", stat: "4-Story Apartments • Transit" },
      { title: "Seaport Village Redevelopment", location: "Waterfront • San Diego", status: "Port Master Plan Update", color: "yellow", image: "https://images.unsplash.com/photo-1505245208761-ba872912fac0?auto=format&fit=crop&w=800&q=80", stat: "Retail/Hotel • Tourism" }
    ]
  }
];

// --- HELPER: Styles for Badges ---
const getBadgeStyles = (color: string) => {
  switch(color) {
    case 'red': return { bg: '#fee2e2', text: '#991b1b', border: '#fecaca', dot: '#991b1b' };
    case 'yellow': return { bg: '#fef9c3', text: '#854d0e', border: '#fde047', dot: '#854d0e' };
    case 'green': return { bg: '#dcfce7', text: '#166534', border: '#bbf7d0', dot: '#166534' };
    case 'blue': return { bg: '#e0f2fe', text: '#0D47A1', border: '#bae6fd', dot: '#0D47A1' };
    default: return { bg: '#eee', text: '#333', border: '#ccc', dot: '#333' };
  }
};

// --- SUB-COMPONENT: Subscribe Form ---
const SubscribeForm = ({ variant }: { variant: 'hero' | 'footer' }) => {
  const [email, setEmail] = useState<string>('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error' | 'rate-limited' | 'disposable'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 1. RATE LIMIT CHECK
    const lastSubTime = localStorage.getItem('resitus_last_sub');
    if (lastSubTime) {
      const minutesSince = (Date.now() - parseInt(lastSubTime)) / 1000 / 60;
      if (minutesSince < 1) { 
        setStatus('rate-limited');
        return;
      }
    }

    // 2. DISPOSABLE EMAIL CHECK
    const domain = email.split('@')[1];
    if (domain && DISPOSABLE_DOMAINS.includes(domain.toLowerCase())) {
      setStatus('disposable');
      return;
    }

    setStatus('loading');

    try {
      const formBody = new URLSearchParams();
      formBody.append('email', email);
      formBody.append('userGroup', 'Waitlist');

      const response = await fetch(`https://app.loops.so/api/newsletter-form/${LOOPS_FORM_ID}`, {
        method: 'POST',
        body: formBody,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.ok) {
        setStatus('success');
        localStorage.setItem('resitus_last_sub', Date.now().toString());
        setEmail('');
      } else {
        setStatus('error');
      }
    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '10px', 
        backgroundColor: '#ecfdf5', 
        padding: '12px 16px', 
        borderRadius: '8px', 
        border: '1px solid #bbf7d0', 
        maxWidth: variant === 'hero' ? '500px' : '450px',
        margin: variant === 'footer' ? '0 auto' : '0' 
      }}>
        <CheckIcon />
        <span style={{ color: '#166534', fontWeight: 600 }}>You are on the list! Check your inbox.</span>
      </div>
    );
  }

  // CONTAINER STYLE: Keeps side-by-side
  const containerStyle = variant === 'hero' 
    ? { display: 'flex', gap: '10px', maxWidth: '500px', width: '100%' }
    : { display: 'flex', gap: '10px', justifyContent: 'center', maxWidth: '450px', margin: '0 auto', flexWrap: 'wrap' as const };

  // INPUT STYLE: 'minWidth: 0' allows it to shrink so the button fits
  const inputStyle = variant === 'hero'
    ? { flex: 1, minWidth: 0, padding: '16px', fontSize: '16px', border: 'none', borderRadius: '4px', outline: 'none', backgroundColor: 'white', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }
    : { flex: '1 1 250px', padding: '16px', fontSize: '16px', border: '1px solid #e2e8f0', borderRadius: '4px', outline: 'none', backgroundColor: 'white' };

  const buttonStyle = {
    backgroundColor: status === 'loading' ? '#64748b' : '#0D47A1',
    color: 'white',
    border: 'none',
    padding: variant === 'hero' ? '0 24px' : '16px 32px',
    fontSize: '14px',
    fontWeight: 700,
    borderRadius: '4px',
    cursor: status === 'loading' ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    whiteSpace: 'nowrap' as const,
    boxShadow: variant === 'hero' ? '0 4px 6px rgba(0,0,0,0.2)' : 'none'
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <div style={containerStyle}>
        <input 
          type="email" 
          placeholder="Enter your email for early access..." 
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={status === 'loading'}
          style={inputStyle}
        />
        <button type="submit" disabled={status === 'loading'} style={buttonStyle}>
          {status === 'loading' ? 'Joining...' : 'JOIN WAITLIST'}
          {variant === 'hero' && status !== 'loading' && <ArrowRightIcon />}
        </button>
      </div>
      
      {status === 'error' && (
        <p style={{ color: '#ef4444', marginTop: '8px', fontSize: '14px', fontWeight: 600 }}>
          Something went wrong. Please check your email and try again.
        </p>
      )}
      {status === 'rate-limited' && (
        <p style={{ color: '#f59e0b', marginTop: '8px', fontSize: '14px', fontWeight: 600 }}>
          You've already signed up! Please wait a minute before trying again.
        </p>
      )}
      {status === 'disposable' && (
        <p style={{ color: '#ef4444', marginTop: '8px', fontSize: '14px', fontWeight: 600 }}>
          Disposable email addresses are not accepted.
        </p>
      )}
    </form>
  );
};

// --- SUB-COMPONENTS ---

const BlastRadiusSection = () => {
  return (
    <div style={{ backgroundColor: '#fff', padding: '100px 40px', width: '100%' }}>
      <div style={{ maxWidth: '1500px', margin: '0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '60px' }}>
        
        {/* LEFT COLUMN */}
        <div style={{ flex: '0 1 420px' }}>
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '48px', fontWeight: 800, color: '#0D47A1', lineHeight: 1.1, marginBottom: '0' }}>
              Identify Risk.<br />Spot Opportunity.
            </h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <div style={{ marginTop: '6px', width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#ef4444', flexShrink: 0, boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)' }}></div>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#2a2a33', margin: '0 0 4px 0' }}>Traffic & Density</h3>
                <p style={{ margin: 0, color: '#596b82', lineHeight: 1.6 }}>Know if a newly approved 200-unit complex will gridlock your street.</p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <div style={{ marginTop: '6px', width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#facc15', flexShrink: 0, boxShadow: '0 2px 4px rgba(250, 204, 21, 0.3)' }}></div>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#2a2a33', margin: '0 0 4px 0' }}>Neighborhood Votes</h3>
                <p style={{ margin: 0, color: '#596b82', lineHeight: 1.6 }}>Track active variance requests for large property expansions before they are approved.</p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <div style={{ marginTop: '6px', width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#22c55e', flexShrink: 0, boxShadow: '0 2px 4px rgba(34, 197, 94, 0.3)' }}></div>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#2a2a33', margin: '0 0 4px 0' }}>Active Construction</h3>
                <p style={{ margin: 0, color: '#596b82', lineHeight: 1.6 }}>See permits for major home additions or excavation happening next door.</p>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div style={{ flex: '1 1 750px', position: 'relative' }}>
          <div style={{ 
            borderRadius: '16px', 
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', 
            border: '1px solid #e5e7eb', 
            overflow: 'hidden', 
            backgroundColor: '#f9fafb' 
          }}>
            <video 
              autoPlay 
              loop 
              muted 
              playsInline 
              style={{ width: '100%', height: 'auto', display: 'block' }}
            >
              <source src="/assets/ResituesAnimation.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          <div style={{ 
            position: 'absolute', inset: '-10px', backgroundColor: 'rgba(59, 130, 246, 0.1)', 
            zIndex: -1, filter: 'blur(20px)', borderRadius: '16px' 
          }}></div>
        </div>

      </div>
    </div>
  );
};

const PersonaCardsSection = () => {
  return (
    <div style={{ backgroundColor: '#F8F9FA', padding: '100px 40px', width: '100%' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <h2 style={{ fontSize: '36px', fontWeight: 800, color: '#0D47A1', margin: 0 }}>
            Decision-grade data for every stakeholder.
          </h2>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '40px', justifyContent: 'center' }}>
          
          {/* CARD A: Homebuyers */}
          <div style={{ flex: '1 1 450px', backgroundColor: '#fff', borderRadius: '16px', boxShadow: '0 10px 40px -10px rgba(0,0,0,0.08)', padding: '40px', display: 'flex', flexDirection: 'column', border: '1px solid #eef2f6' }}>
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ fontSize: '24px', fontWeight: 800, color: '#0D47A1', marginBottom: '12px' }}>Protect your peace of mind.</h3>
              <p style={{ fontSize: '16px', color: '#596b82', lineHeight: 1.6, margin: 0 }}>A home is your biggest asset — don't get blindsided by construction noise, traffic jams, or blocked views after you move in.</p>
            </div>
            <div style={{ backgroundColor: '#f1f5f9', borderRadius: '12px', padding: '20px', marginTop: 'auto', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', marginBottom: '16px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Impact Analysis</div>
              <div style={{ display: 'grid', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#fff', padding: '10px 14px', borderRadius: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: '90px' }}><span style={{ fontSize: '16px' }}>⚠️</span><span style={{ fontWeight: 700, color: '#334155', fontSize: '14px' }}>Noise</span></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, justifyContent: 'flex-end' }}><span style={{ fontSize: '10px', fontWeight: 700, backgroundColor: '#fee2e2', color: '#991b1b', padding: '3px 6px', borderRadius: '4px', whiteSpace: 'nowrap' }}>CRITICAL</span><span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500, textAlign: 'right' }}>Rock drilling planned (4 months)</span></div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#fff', padding: '10px 14px', borderRadius: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: '90px' }}><span style={{ fontSize: '16px' }}>🚗</span><span style={{ fontWeight: 700, color: '#334155', fontSize: '14px' }}>Traffic</span></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, justifyContent: 'flex-end' }}><span style={{ fontSize: '10px', fontWeight: 700, backgroundColor: '#fee2e2', color: '#991b1b', padding: '3px 6px', borderRadius: '4px', whiteSpace: 'nowrap' }}>HIGH</span><span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500, textAlign: 'right' }}>108 new cars at rush hour</span></div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#fff', padding: '10px 14px', borderRadius: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: '90px' }}><span style={{ fontSize: '16px' }}>☀️</span><span style={{ fontWeight: 700, color: '#334155', fontSize: '14px' }}>Sunlight</span></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, justifyContent: 'flex-end' }}><span style={{ fontSize: '10px', fontWeight: 700, backgroundColor: '#ffedd5', color: '#c2410c', padding: '3px 6px', borderRadius: '4px', whiteSpace: 'nowrap' }}>RISK</span><span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500, textAlign: 'right' }}>New building blocks afternoon sun</span></div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#fff', padding: '10px 14px', borderRadius: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: '90px' }}><span style={{ fontSize: '16px' }}>🎒</span><span style={{ fontWeight: 700, color: '#334155', fontSize: '14px' }}>Schools</span></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, justifyContent: 'flex-end' }}><span style={{ fontSize: '10px', fontWeight: 700, backgroundColor: '#dcfce7', color: '#166534', padding: '3px 6px', borderRadius: '4px', whiteSpace: 'nowrap' }}>SAFE</span><span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500, textAlign: 'right' }}>No overcrowding predicted</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* CARD B: Investors */}
          <div style={{ flex: '1 1 450px', backgroundColor: '#fff', borderRadius: '16px', boxShadow: '0 10px 40px -10px rgba(0,0,0,0.08)', padding: '40px', display: 'flex', flexDirection: 'column', border: '1px solid #eef2f6' }}>
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ fontSize: '24px', fontWeight: 800, color: '#0D47A1', marginBottom: '12px' }}>Find the next hot neighborhood.</h3>
              <p style={{ fontSize: '16px', color: '#596b82', lineHeight: 1.6, margin: 0 }}>Resitus helps you spot neighborhoods that are about to increase in value before it becomes obvious.</p>
            </div>
            <div style={{ backgroundColor: '#f1f5f9', borderRadius: '12px', padding: '20px', marginTop: 'auto', border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', marginBottom: '16px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Investment Analysis</div>
              <div style={{ display: 'grid', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#fff', padding: '10px 14px', borderRadius: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: '90px' }}><span style={{ fontSize: '16px' }}>💰</span><span style={{ fontWeight: 700, color: '#334155', fontSize: '14px' }}>Rent</span></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, justifyContent: 'flex-end' }}><span style={{ fontSize: '10px', fontWeight: 700, backgroundColor: '#dcfce7', color: '#166534', padding: '3px 6px', borderRadius: '4px', whiteSpace: 'nowrap' }}>HIGH</span><span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500, textAlign: 'right' }}>15% higher than street avg</span></div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#fff', padding: '10px 14px', borderRadius: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: '90px' }}><span style={{ fontSize: '16px' }}>🏗️</span><span style={{ fontWeight: 700, color: '#334155', fontSize: '14px' }}>Zoning</span></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, justifyContent: 'flex-end' }}><span style={{ fontSize: '10px', fontWeight: 700, backgroundColor: '#e0f2fe', color: '#0369a1', padding: '3px 6px', borderRadius: '4px', whiteSpace: 'nowrap' }}>CHANGED</span><span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500, textAlign: 'right' }}>Allowed to build 4 stories now</span></div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#fff', padding: '10px 14px', borderRadius: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: '90px' }}><span style={{ fontSize: '16px' }}>📈</span><span style={{ fontWeight: 700, color: '#334155', fontSize: '14px' }}>Value</span></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, justifyContent: 'flex-end' }}><span style={{ fontSize: '10px', fontWeight: 700, backgroundColor: '#dcfce7', color: '#166534', padding: '3px 6px', borderRadius: '4px', whiteSpace: 'nowrap' }}>RISING</span><span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500, textAlign: 'right' }}>Luxury anchor project approved</span></div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#fff', padding: '10px 14px', borderRadius: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: '90px' }}><span style={{ fontSize: '16px' }}>🚀</span><span style={{ fontWeight: 700, color: '#334155', fontSize: '14px' }}>Timing</span></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, justifyContent: 'flex-end' }}><span style={{ fontSize: '10px', fontWeight: 700, backgroundColor: '#e0f2fe', color: '#0369a1', padding: '3px 6px', borderRadius: '4px', whiteSpace: 'nowrap' }}>EARLY</span><span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500, textAlign: 'right' }}>Permits filed 2 days ago</span></div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

// --- SUB-COMPONENT: Section 5 (FAQ & Footer) ---
const FaqAndFooter = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [isContactOpen, setIsContactOpen] = useState(false);

  const toggle = (i: number) => {
    setOpenIndex(openIndex === i ? null : i);
  };

  const FAQS = [
    {
      q: "Where does this data come from?",
      a: "We provide institutional-grade intelligence confirmed by regulatory approvals, ensuring accuracy without the rumors."
    },
    {
      q: "Is Resitus free?",
      a: "Yes. Resitus is currently free. We believe every homebuyer and investor deserves access to the future of their neighborhood, not just the past."
    },
    {
      q: "Is my city supported?",
      a: "We are rapidly expanding our coverage map nationwide. Join the waitlist to secure early access for the beta launch."
    }
  ];

  return (
    <div style={{ backgroundColor: '#fff', padding: '100px 40px 40px', width: '100%', borderTop: '1px solid #f1f5f9' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        
        {/* FAQ Header */}
        <h2 style={{ fontSize: '32px', fontWeight: 800, color: '#0D47A1', marginBottom: '40px', textAlign: 'center' }}>
          Frequently Asked Questions
        </h2>

        {/* FAQ Items */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '80px' }}>
          {FAQS.map((item, i) => (
            <div key={i} style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '16px' }}>
              <button 
                onClick={() => toggle(i)}
                style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer', padding: '12px 0', textAlign: 'left' }}
              >
                <span style={{ fontSize: '18px', fontWeight: 700, color: '#2a2a33' }}>{item.q}</span>
                {openIndex === i ? <MinusIcon /> : <PlusIcon />}
              </button>
              {openIndex === i && (
                <div style={{ fontSize: '16px', color: '#596b82', lineHeight: 1.6, marginTop: '8px', paddingRight: '20px' }}>
                  {item.a}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Final CTA */}
        <div style={{ 
          backgroundColor: '#EFF6FF', 
          borderRadius: '24px', 
          padding: '48px 24px', 
          textAlign: 'center', 
          marginBottom: '80px' 
        }}>
          <h2 style={{ fontSize: '36px', fontWeight: 800, color: '#0D47A1', marginBottom: '16px', lineHeight: 1.2 }}>
            Stop guessing. Start knowing.
          </h2>
          <p style={{ fontSize: '18px', color: '#596b82', marginBottom: '32px' }}>
            Join the waitlist to see the future of your neighborhood.
          </p>
          <SubscribeForm variant="footer" />
        </div>

        {/* Footer */}
        <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontWeight: 700, color: '#000', fontSize: '18px' }}>
              Resitus<span style={{ color: '#4dabf7' }}>.</span>
            </span>
            <span style={{ fontSize: '12px', color: '#94a3b8' }}>© 2025 Resitus Inc. All rights reserved.</span>
          </div>
          <div style={{ display: 'flex', gap: '24px' }}>
            <button onClick={() => navigate('/privacy')} style={{ fontSize: '14px', color: '#64748b', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}>Privacy Policy</button>
            <button onClick={() => navigate('/terms')} style={{ fontSize: '14px', color: '#64748b', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}>Terms of Use</button>
            <button onClick={() => setIsContactOpen(true)} style={{ fontSize: '14px', color: '#64748b', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}>Contact Support</button>
          </div>
        </div>

      </div>

      {/* RENDER MODAL */}
      {isContactOpen && <ContactModal onClose={() => setIsContactOpen(false)} />}
    </div>
  );
};

// --- MAIN PAGE ---
const LandingPage = () => {
  const [index, setIndex] = useState<number>(0);
  const [fade, setFade] = useState<boolean>(true);

  // Auto-advance logic with STRICT SYNC (8000ms total loop)
  useEffect(() => {
    const interval = setInterval(() => {
      // 1. Trigger Fade Out
      setFade(false);
      
      // 2. Wait 1.5s (match CSS transition) then Swap Data
      setTimeout(() => {
        setIndex((prevIndex) => (prevIndex + 1) % MARKET_DATA.length);
        // 3. Trigger Fade In immediately after swap
        setFade(true);
      }, 1500);

    }, 8000); // EXACT match with CSS animation duration

    return () => clearInterval(interval);
  }, []);

  const currentCity = MARKET_DATA[index];

  return (
    <div style={{ color: '#2a2a33', backgroundColor: '#fff' }}>
      
      {/* Keyframe Animation for the Timer Bar */}
      <style>
        {`
          @keyframes fillBar {
            from { width: 0%; }
            to { width: 100%; }
          }
          .timer-bar-fill {
            /* 8000ms duration, LINEAR easing for perfectly smooth motion */
            animation: fillBar 8000ms linear forwards;
          }
          .market-grid {
            display: grid;
            grid-template-columns: 1fr;
            gap: 20px;
            transition: opacity 1500ms ease-in-out, transform 1500ms ease-in-out;
          }
          @media (min-width: 600px) {
            .market-grid {
              grid-template-columns: repeat(2, 1fr);
            }
          }
          @media (min-width: 1024px) {
            .market-grid {
              grid-template-columns: repeat(4, 1fr);
            }
          }

          /* --- NEW CLASS FOR HERO SECTION --- */
          /* Default (Mobile): Even side padding */
          .hero-section {
            position: relative;
            height: 650px;
            display: flex;
            align-items: center;
            justify-content: flex-start;
            overflow: hidden;
            
            /* MOBILE PADDING: Even borders */
            padding-left: 20px;
            padding-right: 20px;
            
            /* Fix for padding overflow */
            box-sizing: border-box;
          }

          /* Desktop: Restore original design */
          @media (min-width: 768px) {
            .hero-section {
              padding-left: 8%; /* Original left padding */
              padding-right: 0;   /* Unbounded right side */
            }
          }
        `}
      </style>

      {/* --- NAVBAR --- */}
      <nav style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        padding: '20px 40px',
        position: 'absolute',
        top: 0, left: 0, right: 0,
        zIndex: 100,
        background: 'linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 100%)'
      }}>
        <div style={{ color: '#fff', fontWeight: 900, fontSize: '28px', letterSpacing: '-0.5px', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
          Resitus<span style={{ color: '#4dabf7' }}>.</span>
        </div>
        <div></div>
      </nav>

      {/* --- HERO SECTION --- */}
      {/* ADDED CLASSNAME, REMOVED INLINE PADDING STYLES */}
      <div className="hero-section">
        <div style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundImage: 'url("/assets/LandingPageBackgroundImage.jpeg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          zIndex: 0
        }}></div>
        <div style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.4)',
          zIndex: 1
        }}></div>

        <div style={{ zIndex: 2, width: '100%', maxWidth: '650px', textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>
          <h1 style={{ fontSize: '56px', fontWeight: 800, color: '#fff', marginBottom: '20px', lineHeight: 1.1, letterSpacing: '-1.5px' }}>
            Zoning. Permits. <br/> Variances. <span style={{ color: '#4dabf7' }}>Future.</span>
          </h1>
          <p style={{ fontSize: '20px', color: '#fff', lineHeight: '1.5', marginBottom: '40px', fontWeight: 500 }}>
            Discover the massive projects being built next door before ground is broken.
          </p>
          <SubscribeForm variant="hero" />
        </div>
      </div>

      {/* --- MARKET INTELLIGENCE CAROUSEL --- */}
      <div style={{ padding: '60px 40px', backgroundColor: '#F8F9FA' }}>
        
        {/* Dynamic Header (Fixed Anchor + Dynamic Fade) */}
        <div style={{ marginBottom: '10px', marginLeft: '5px' }}>
          <h2 style={{ 
            fontSize: '28px', 
            fontWeight: 800, 
            margin: '0 0 5px 0', 
            color: '#2a2a33', 
            letterSpacing: '-0.5px', 
            display: 'flex', 
            alignItems: 'baseline', 
            gap: '8px',
            flexWrap: 'wrap'
          }}>
            {/* Part A: Static Anchor */}
            <span style={{ color: '#0D47A1' }}>Active Proposals:</span>
            
            {/* Part B: Dynamic Metro Name */}
            <span style={{ 
              color: '#0D47A1',
              opacity: fade ? 1 : 0, 
              transform: fade ? 'translateY(0)' : 'translateY(2px)', 
              transition: 'all 500ms ease-in-out', 
            }}>
              {currentCity.name}
            </span>
          </h2>
          <p style={{ fontSize: '16px', color: '#596b82', margin: 0, fontWeight: 500 }}>
            Tracking high-impact projects from proposal to permit in real-time.
          </p>
        </div>

        {/* Timer Bar (Ultra Thin & Light Blue) */}
        <div style={{ width: '100%', height: '2px', backgroundColor: '#e5e7eb', marginBottom: '30px', marginTop: '10px', borderRadius: '1px', overflow: 'hidden' }}>
          {/* Key forces component remount to restart animation */}
          <div key={index} className="timer-bar-fill" style={{ height: '100%', backgroundColor: '#B3E5FC' }}></div>
        </div>

        {/* Card Grid (Responsive) */}
        <div 
          className="market-grid"
          style={{ 
            opacity: fade ? 1 : 0,
            transform: fade ? 'translateY(0)' : 'translateY(10px)',
          }}
        >
          {currentCity.projects.map((project, i) => {
            const styles = getBadgeStyles(project.color);
            return (
              <div key={i} style={{ backgroundColor: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden', cursor: 'default', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <div style={{ height: '180px', backgroundColor: '#eee', position: 'relative' }}>
                  <img 
                    src={project.image} 
                    alt={project.title} 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                  <div style={{ 
                    position: 'absolute', top: '10px', left: '10px', 
                    backgroundColor: styles.bg, color: styles.text, border: `1px solid ${styles.border}`,
                    padding: '3px 8px', fontSize: '10px', fontWeight: 800, borderRadius: '20px', 
                    textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '5px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <span style={{ width: '6px', height: '6px', backgroundColor: styles.dot, borderRadius: '50%', display: 'inline-block' }}></span>
                    {project.status}
                  </div>
                </div>
                <div style={{ padding: '16px' }}>
                  <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '4px', color: '#2a2a33', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{project.title}</div>
                  <div style={{ fontSize: '12px', color: '#596b82', fontWeight: 600, marginBottom: '8px' }}>{project.location}</div>
                  <div style={{ paddingTop: '8px', borderTop: '1px solid #eee', fontSize: '12px', color: '#0D47A1', fontWeight: 700 }}>
                    {project.stat}
                  </div>
                </div>
              </div>
            );
          })}

        </div>
      </div>

      {/* --- SECTION 3: BLAST RADIUS DEMO --- */}
      <BlastRadiusSection />

      {/* --- SECTION 4: PERSONA CARDS --- */}
      <PersonaCardsSection />

      {/* --- SECTION 5 & 6 & FOOTER --- */}
      <FaqAndFooter />

    </div>
  );
};

export default LandingPage;